import { useCallback, useEffect, useState } from 'react';
import { throttle } from 'lodash';

export enum ScrollDirection {
  Up,
  Down,
}

/**
 * This custom hook listens to scroll event on the target element
 * to determine the scroll direction and current scroll position.
 */
export default function useScrollDirection(
  target: React.MutableRefObject<null | HTMLElement>,
  throttleInMs: number = 3000
) {
  const [scrollPosition, setScrollPosition] = useState(
    target.current?.scrollTop ?? 0
  );
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>();

  const handleScroll = useCallback(
    throttle(() => {
      const currentElement = target.current;

      if (currentElement) {
        const scrollTop = currentElement.scrollTop;

        if (scrollTop > scrollPosition) {
          setScrollDirection(ScrollDirection.Down);
        } else {
          setScrollDirection(ScrollDirection.Up);
        }

        setScrollPosition(scrollTop);
      }
    }, throttleInMs),
    [target, scrollPosition]
  );

  useEffect(() => {
    const currentElement = target.current;

    if (currentElement) {
      currentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      currentElement?.removeEventListener('scroll', handleScroll);
    };
  }, [target, handleScroll]);

  return {
    scrollPosition,
    scrollDirection,
  };
}
